(() => {
  'use strict';
  const TYPE = 'filter.p.product_type';
  const decode = value => String(value).replace(/&amp;/g, '&');
  const unique = values => [...new Set(values.filter(Boolean))];
  function index(english, localized, translations = localized) {
    const keys = new Map(english.map(p => [String(p.id), decode(p.category)]));
    if (keys.size !== english.length || localized.length !== english.length || localized.some(p => !keys.has(String(p.id)))) throw Error('Incomplete language identity');
    const groups = new Map(), aliases = new Map();
    for (const p of localized) {
      const key = keys.get(String(p.id));
      if (!key) continue;
      if (!groups.has(key)) groups.set(key, { key, values: new Set([key]), names: new Map() });
      const group = groups.get(key);
      group.values.add(decode(p.category));
      group.names.set(decode(p.category), (group.names.get(decode(p.category)) || 0) + 1);
    }
    if (translations.length !== english.length || translations.some(p => !keys.has(String(p.id)))) throw Error('Incomplete translated identity');
    for (const p of translations) {
      const group = groups.get(keys.get(String(p.id)));
      if (group) group.values.add(decode(p.category));
    }
    for (const [key, group] of groups) {
      group.label = [...group.names].sort((a, b) => b[1] - a[1])[0]?.[0] || key;
      for (const value of group.values) {
        if (aliases.has(value) && aliases.get(value) !== key) throw Error('Ambiguous type translation');
        aliases.set(value, key);
      }
    }
    return { keys, groups, aliases };
  }
  function read(params, identity) {
    const explicit = params.getAll('gb_type').map(decode);
    const values = explicit.length ? explicit : params.getAll(TYPE).map(decode).flatMap(value => identity.aliases.has(value) ? [value] : value.split(','));
    return unique(values.map(value => identity.aliases.get(value) || value));
  }
  function write(url, selected, identity, resetPage = true) {
    url.searchParams.delete(TYPE);
    url.searchParams.delete('gb_type');
    if (resetPage) url.searchParams.delete('page');
    for (const value of unique(selected.flatMap(key => [...(identity.groups.get(key)?.values || [key])]))) url.searchParams.append(TYPE, value);
    return url;
  }
  const englishPath = path => path.replace(/^\/zh-hk(?=\/|$)/, '/en-hk').replace(/^\/zh(?:-TW)?(?=\/|$)/i, '');
  const chinesePath = path => path.startsWith('/en-hk/') ? path.replace('/en-hk/', '/zh-hk/') : '/zh' + path.replace(/^\/en(?=\/)/, '');
  const cache = new Map();
  async function catalogue(path, view = 'gb-type-index', preserveFilters = false) {
    const url = new URL(path, location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (!preserveFilters) url.search = '';
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.set('view', view);
    const fetchPage = async page => {
      url.searchParams.set('page', String(page));
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw Error('Type index unavailable');
      const data = await response.json();
      if (!Array.isArray(data.products) || !Number.isInteger(data.pages) || !Number.isInteger(data.count) || data.pages > 100) throw Error('Invalid type index');
      return data;
    };
    const first = await fetchPage(1), products = [...first.products];
    for (let page = 2; page <= first.pages; page++) products.push(...(await fetchPage(page)).products);
    if (products.length !== first.count || new Set(products.map(p => String(p.id))).size !== products.length) throw Error('Incomplete type index');
    return products;
  }
  function identityFor(path) {
    if (cache.has(path)) return cache.get(path);
    const promise = (async () => {
      const localized = await catalogue(path), canonicalPath = englishPath(path);
      return canonicalPath === path ? index(localized, localized, await catalogue(chinesePath(path))) : index(await catalogue(canonicalPath), localized);
    })();
    cache.set(path, promise);
    promise.catch(() => cache.delete(path));
    return promise;
  }
  globalThis.GbTypes = { TYPE, index, read, write, englishPath, chinesePath, catalogue, identityFor };
  if (typeof document === 'undefined') return;
  const runs = new WeakSet();
  let currentIdentity = null;
  function selectedUrl(selected, identity) { return write(new URL(location.href), selected, identity); }
  function enhanceNative(nav, identity) {
    const selected = read(new URL(location.href).searchParams, identity);
    const canonicalUrl = write(new URL(location.href), selected, identity, false);
    const before = new URL(location.href).searchParams.getAll(TYPE).sort().join('\u0000');
    const after = canonicalUrl.searchParams.getAll(TYPE).sort().join('\u0000');
    if (before !== after || new URL(location.href).searchParams.has('gb_type')) {
      location.replace(canonicalUrl.href);
      return;
    }
    currentIdentity = identity;
    if (identity.serverRules && nav.dataset.gbServerTypes === 'true') { nav.dataset.gbTypesReady = 'true'; nav.removeAttribute('aria-busy'); return; }
    const facets = JSON.parse(nav.querySelector('[data-gb-type-values]').textContent);
    const counts = new Map();
    for (const value of facets) {
      const key = identity.aliases.get(decode(value.value)) || decode(value.value);
      counts.set(key, (counts.get(key) || 0) + value.count);
    }
    for (const key of selected) if (!counts.has(key)) counts.set(key, 0);
    const all = nav.querySelector('a[data-gb-type=""]');
    all.href = selectedUrl([], identity).href;
    if (selected.length) all.removeAttribute('aria-current'); else all.setAttribute('aria-current', 'true');
    nav.querySelectorAll('a[data-gb-type]:not([data-gb-type=""])').forEach(a => a.remove());
    for (const [key, count] of counts) {
      const a = document.createElement('a');
      a.dataset.gbType = key;
      a.href = selectedUrl([key], identity).href;
      a.textContent = identity.groups.get(key)?.label || key;
      if (selected.includes(key)) a.setAttribute('aria-current', 'true');
      const small = document.createElement('small');
      small.textContent = String(count);
      a.append(' ', small);
      nav.append(a);
    }
    document.querySelectorAll('.filter-form').forEach(form => {
      const inputs = [...form.querySelectorAll('input[name="' + TYPE + '"]')];
      const lists = new Set(inputs.map(input => input.closest('ul')).filter(Boolean));
      for (const list of lists) {
        list.replaceChildren();
        let number = 0;
        for (const [key, count] of counts) {
          const li = document.createElement('li');
          li.className = 'tag' + (selected.includes(key) ? ' tag--active' : '') + (!count && !selected.includes(key) ? ' hide' : '');
          const label = document.createElement('label');
          label.className = 'tag__checkbox-wrapper text-label';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.className = 'tag__input';
          input.name = TYPE;
          input.value = key;
          input.dataset.gbCanonicalType = key;
          input.id = (form.closest('#FilterDrawer') ? 'GbTypeDrawer-' : 'GbTypeSidebar-') + number++;
          input.checked = selected.includes(key);
          label.htmlFor = input.id;
          const box = document.createElement('span');
          box.className = 'tag__checkbox';
          const text = document.createElement('span');
          text.className = 'tag__text';
          text.textContent = identity.groups.get(key)?.label || key;
          label.append(input, box, text, ' (' + count + ')');
          li.append(label);
          list.append(li);
          if (input.checked) for (const alias of identity.groups.get(key)?.values || []) {
            if (alias === key) continue;
            const hidden = document.createElement('input');
            hidden.type = 'hidden'; hidden.name = TYPE; hidden.value = alias;
            li.append(hidden);
          }
        }
      }
    });
    document.querySelectorAll('.tag-list--active-tags').forEach(list => {
      const seen = new Set();
      list.querySelectorAll('a[href]').forEach(a => {
        const raw = new URL(a.href, location.origin).searchParams.getAll(TYPE);
        const missing = selected.find(key => [...(identity.groups.get(key)?.values || [key])].some(alias => !raw.includes(alias)));
        if (!missing) return;
        const li = a.closest('li');
        if (seen.has(missing)) { li.remove(); return; }
        seen.add(missing);
        a.textContent = identity.groups.get(missing)?.label || missing;
        a.href = selectedUrl(selected.filter(key => key !== missing), identity).href;
      });
    });
    nav.dataset.gbTypesReady = 'true';
    nav.removeAttribute('aria-busy');
  }
  function serverIdentity(nav) {
    if (!Array.isArray(globalThis.GbTypeRules)) return null;
    const groups = new Map(), aliases = new Map();
    for (const rule of globalThis.GbTypeRules) {
      groups.set(rule.key, { key: rule.key, values: new Set(rule.values), label: document.documentElement.lang.startsWith('zh') ? rule.zh : rule.key });
      for (const value of rule.values) aliases.set(value, rule.key);
    }
    const facets = JSON.parse(nav.querySelector('[data-gb-type-values]').textContent);
    if (facets.some(value => !aliases.has(decode(value.value)))) return null;
    return { keys: new Map(), groups, aliases, serverRules: true };
  }
  async function native(nav) {
    if (runs.has(nav)) return;
    runs.add(nav);
    nav.setAttribute('aria-busy', 'true');
    try { const known = serverIdentity(nav); enhanceNative(nav, known || await identityFor(nav.dataset.gbCollectionUrl)); }
    catch {
      nav.dataset.gbTypesReady = 'failed';
      nav.removeAttribute('aria-busy');
      const button = document.createElement('button');
      button.type = 'button'; button.textContent = nav.dataset.gbRetry;
      button.addEventListener('click', () => { button.remove(); runs.delete(nav); native(nav); });
      nav.append(button);
    }
  }
  const scan = () => { document.querySelectorAll('.gb-native-types[data-gb-collection-url]').forEach(native); document.querySelectorAll('.filter-form').forEach(form => { if (!form.querySelector('[name="sort_by"]')) { const sort = new URL(location.href).searchParams.get('sort_by'); if (sort) { const input = document.createElement('input'); input.type = 'hidden'; input.name = 'sort_by'; input.value = sort; form.append(input); } } }); };
  document.addEventListener('input', event => {
    const input = event.target.closest('[data-gb-canonical-type]');
    if (!input || !currentIdentity) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const selected = new Set(read(new URL(location.href).searchParams, currentIdentity));
    if (input.checked) selected.add(input.dataset.gbCanonicalType); else selected.delete(input.dataset.gbCanonicalType);
    location.assign(selectedUrl([...selected], currentIdentity).href);
  }, true);
  function syncLocaleReturn(form) {
    if (!form || !form.matches('form[action*="localization"]')) return;
    form.querySelectorAll('input[name="return_to"]').forEach(input => { input.value = location.pathname + location.search + location.hash; });
  }
  document.addEventListener('click', event => {
    const control = event.target.closest('[data-disclosure-option],button[name="language_code"]');
    if (control) syncLocaleReturn(control.closest('form'));
  }, true);
  document.addEventListener('submit', event => syncLocaleReturn(event.target), true);
  document.addEventListener('collection:reloaded', scan);
  document.addEventListener('DOMContentLoaded', scan);
  if (document.readyState !== 'loading') scan();
})();
